import { useCallback, useMemo, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCurrentCollectionStore } from '@/stores/currentCollection'
import {
    useAddProductToCollection,
    useRemoveProductFromCollection,
    useCollection,
} from '@/hooks/api/useCollections'
import { useProtectedAction } from '@/hooks/useProtectedAction'
import { useToast } from '@/components/shared/toast/Toast'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useUserCollections } from '@/hooks/api/useUsers'
import type { ProductDto } from '@wtf/shared'

/**
 * Hook to handle toggling products in the currently selected collection.
 * This replaces the old "like" functionality with collection-specific saving.
 */
export const useCurrentCollectionToggle = (
    productData: ProductDto | undefined
) => {
    const queryClient = useQueryClient()
    const { withAuth } = useProtectedAction()
    const { addToast } = useToast()
    const { user } = useCurrentUser()
    // Use Zustand subscription to ensure React re-renders when store updates
    const currentCollectionId = useCurrentCollectionStore(
        (state) => state.currentCollectionId
    )
    const { hasCurrentCollection, setCurrentCollection } =
        useCurrentCollectionStore()

    // Get user's collections to set default if needed
    const { data: userCollectionIds = [], isLoading: collectionsLoading } =
        useUserCollections(user?.uid)

    const addMutation = useAddProductToCollection()
    const removeMutation = useRemoveProductFromCollection()

    // Auto-set first collection as default if none selected
    useEffect(() => {
        // Only attempt auto-selection if collections have finished loading
        if (collectionsLoading) return

        if (!currentCollectionId && userCollectionIds.length > 0 && user) {
            const firstCollectionId = userCollectionIds[0]
            setCurrentCollection(firstCollectionId)
        }
    }, [
        currentCollectionId,
        userCollectionIds,
        user,
        setCurrentCollection,
        collectionsLoading,
    ])

    // Get current collection data using the same hook as DesktopOverlay
    const { data: currentCollection, isLoading: collectionLoading } =
        useCollection(currentCollectionId || '')

    // Check if product is in current collection
    const isProductInCurrentCollection = useMemo(() => {
        if (!productData || !currentCollection) return false

        const currentUrlId = productData.urlId
        const collectionProductIds = currentCollection.productIds || []

        // Method 1: Direct ID check (fast path)
        // Check if the exact product ID is in the collection
        if (collectionProductIds.includes(productData.id)) {
            return true
        }

        // Method 2: URL ID check (for copied products)
        // Check if any product in the collection has the same urlId
        const productRecords = collectionProductIds
            .map((pid) =>
                queryClient.getQueryData<ProductDto>(['product', pid])
            )
            .filter((p): p is ProductDto => !!p)

        const hasMatchingUrlId = productRecords.some(
            (p) => p.urlId === currentUrlId
        )

        // Method 3: Fallback for cache misses
        // If we don't have all product data in cache, assume the collection data is authoritative
        const allProductsLoaded =
            productRecords.length === collectionProductIds.length

        if (!allProductsLoaded) {
            // Some products aren't loaded in cache yet
            // Fall back to checking if current product's urlId matches any loaded products
            // or if the direct ID is in the collection (already checked above)
            return hasMatchingUrlId
        }

        return hasMatchingUrlId
    }, [productData, currentCollection, queryClient])

    // Get the actual product record in the collection (for removal)
    const productInCollection = useMemo(() => {
        if (!productData || !currentCollection) return null

        const currentUrlId = productData.urlId
        const collectionProductIds = currentCollection.productIds || []

        // First try direct ID match
        if (collectionProductIds.includes(productData.id)) {
            return productData // Return the current product if it's directly in the collection
        }

        // Then check for products with same urlId
        const productRecords = collectionProductIds
            .map((pid) =>
                queryClient.getQueryData<ProductDto>(['product', pid])
            )
            .filter((p): p is ProductDto => !!p)

        return productRecords.find((p) => p.urlId === currentUrlId) || null
    }, [productData, currentCollection, queryClient])

    const handleToggle = useCallback(() => {
        if (!productData) return
        if (!currentCollectionId || !currentCollection) {
            addToast(
                'No collection selected. Please select a collection first.',
                'error',
                3000
            )
            return
        }

        const collectionName = currentCollection.name || 'Collection'

        if (isProductInCurrentCollection && productInCollection) {
            // Remove from collection
            removeMutation.mutate(
                {
                    collectionId: currentCollectionId,
                    productId: productInCollection.id,
                },
                {
                    onSuccess: () => {
                        addToast(
                            `Removed from ${collectionName}`,
                            'success',
                            3000
                        )
                    },
                    onError: () => {
                        addToast(
                            `Failed to remove from ${collectionName}`,
                            'error',
                            3000
                        )
                    },
                }
            )
        } else {
            // Add to collection
            addMutation.mutate(
                {
                    collectionId: currentCollectionId,
                    data: { productId: productData.id },
                },
                {
                    onSuccess: () => {
                        addToast(`Added to ${collectionName}`, 'success', 3000)
                    },
                    onError: () => {
                        addToast(
                            `Failed to add to ${collectionName}`,
                            'error',
                            3000
                        )
                    },
                }
            )
        }
    }, [
        productData,
        currentCollectionId,
        currentCollection,
        isProductInCurrentCollection,
        productInCollection,
        addMutation,
        removeMutation,
        addToast,
    ])

    return {
        handleToggle: withAuth(handleToggle),
        isProductInCurrentCollection,
        currentCollection,
        currentCollectionId,
        hasCurrentCollection: hasCurrentCollection(),
        isMutating: addMutation.isPending || removeMutation.isPending,
        isInitialLoading: collectionLoading && !currentCollection,
    }
}
